import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItem, ListItemIcon, ListItemText,
  AppBar, Toolbar, Typography, IconButton, Avatar, Menu,
  MenuItem, Chip, Badge, Popover, List as MuiList, ListItem as MuiListItem,
  Divider, useTheme, useMediaQuery
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import ViewListIcon from '@mui/icons-material/ViewList';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import ChatIcon from '@mui/icons-material/Chat';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import PaletteIcon from '@mui/icons-material/Palette';
import ChatPanel from './ChatPanel';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const drawerWidth = 220;

const TEMAS = [
  {
    id: 'oscuro',
    nombre: 'Oscuro',
    color_fondo:      '#1a1a2e',
    color_superficie: '#16213e',
    color_primario:   '#0f3460',
    color_acento:     '#90caf9',
    color_campo:      '#1f4f96',
    preview: ['#1a1a2e', '#0f3460', '#90caf9'],
  },
  {
    id: 'claro',
    nombre: 'Claro',
    color_fondo:      '#1e2535',
    color_superficie: '#28354a',
    color_primario:   '#3a4f6e',
    color_acento:     '#b3d9ff',
    color_campo:      '#2e5a90',
    preview: ['#1e2535', '#3a4f6e', '#b3d9ff'],
  },
  {
    id: 'clinico',
    nombre: 'Clínico',
    color_fondo:      '#071820',
    color_superficie: '#0d2530',
    color_primario:   '#0e4060',
    color_acento:     '#4dd0e1',
    color_campo:      '#0e5278',
    preview: ['#071820', '#0e4060', '#4dd0e1'],
  },
  {
    id: 'rosa',
    nombre: 'Rosa',
    color_fondo:      '#1a0d14',
    color_superficie: '#2b1525',
    color_primario:   '#ad1457',
    color_acento:     '#f48fb1',
    color_campo:      '#871c50',
    preview: ['#1a0d14', '#ad1457', '#f48fb1'],
  },
];

// Paleta exclusiva para el portal del paciente — azul medio, distinto del staff
const TEMA_PACIENTE = {
  id: 'paciente',
  color_fondo:      '#0c2340',
  color_superficie: '#163a60',
  color_primario:   '#1976d2',
  color_acento:     '#90caf9',
  color_campo:      '#1e62a8',
};

function aplicarTema(tema) {
  const root = document.documentElement;
  root.style.setProperty('--c-fondo',      tema.color_fondo);
  root.style.setProperty('--c-superficie', tema.color_superficie);
  root.style.setProperty('--c-primario',   tema.color_primario);
  root.style.setProperty('--c-acento',     tema.color_acento);
  root.style.setProperty('--c-campo',      tema.color_campo || 'rgba(255,255,255,0.09)');
}

const TIPO_COLORES = {
  info:    { bg: '#0d47a1', text: '#bbdefb' },
  warning: { bg: '#e65100', text: '#fff3e0' },
  success: { bg: '#1b5e20', text: '#c8e6c9' },
  error:   { bg: '#b71c1c', text: '#ffcdd2' },
};

export default function Layout({ children, usuario, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [avisos, setAvisos] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const lastSeenIdRef = React.useRef(0);
  const [enRecepcion, setEnRecepcion] = useState([]);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [temaAnchor, setTemaAnchor] = useState(null);
  const [temaActual, setTemaActual] = useState(() => {
    try {
      const g = localStorage.getItem('tema_usuario_data');
      if (g) {
        const parsed = JSON.parse(g);
        return TEMAS.find(t => t.id === parsed.id)?.id || 'oscuro';
      }
    } catch {}
    return 'oscuro';
  });

  const tieneChat = ['admin', 'recepcionista', 'medico'].includes(usuario.rol);

  // Aplicar tema al montar: paciente → paleta azul portal; staff → preferencia guardada
  // IMPORTANTE: siempre buscar el tema por id en TEMAS para obtener color_campo actualizado
  useEffect(() => {
    if (usuario.rol === 'paciente') {
      aplicarTema(TEMA_PACIENTE);
    } else {
      try {
        const g = localStorage.getItem('tema_usuario_data');
        if (g) {
          const guardado = JSON.parse(g);
          // Buscar por id para obtener la versión completa con color_campo
          const temaCompleto = TEMAS.find(t => t.id === guardado.id) || TEMAS[0];
          aplicarTema(temaCompleto);
        } else {
          aplicarTema(TEMAS[0]);
        }
      } catch { aplicarTema(TEMAS[0]); }
    }
  }, [usuario.rol]);

  // En mobile el drawer arranca cerrado
  useEffect(() => {
    if (isMobile) setOpen(false);
  }, [isMobile]);

  useEffect(() => {
    axios.get(`${API}/api/avisos`)
      .then(r => setAvisos(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (usuario.rol !== 'medico') return;
    const poll = () => {
      axios.get(`${API}/api/turnos/en-recepcion`)
        .then(r => setEnRecepcion(r.data))
        .catch(() => {});
    };
    poll();
    const iv = setInterval(poll, 10000);
    return () => clearInterval(iv);
  }, [usuario.rol]);

  useEffect(() => {
    if (!tieneChat || chatOpen) return;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/chat`, { params: { desde_id: lastSeenIdRef.current } });
        if (r.data.length > 0) {
          lastSeenIdRef.current = r.data[r.data.length - 1].id;
          setUnread(prev => prev + r.data.length);
        }
      } catch {}
    };
    if (lastSeenIdRef.current === 0) {
      axios.get(`${API}/api/chat`).then(r => {
        if (r.data.length > 0) lastSeenIdRef.current = r.data[r.data.length - 1].id;
      }).catch(() => {});
    }
    const interval = setInterval(poll, 15000);
    return () => clearInterval(interval);
  }, [chatOpen, tieneChat]);

  const menu = [
    { text: 'Mis turnos',   icon: <DashboardIcon />,        path: '/panel-paciente', roles: ['paciente'] },
    { text: 'Mis datos',    icon: <PersonIcon />,            path: '/mis-datos',      roles: ['paciente'] },
    { text: 'Calendario',   icon: <CalendarMonthIcon />,     path: '/calendario',     roles: ['admin', 'recepcionista', 'medico'] },
    { text: 'Turnos',       icon: <ViewListIcon />,          path: '/turnos',         roles: ['admin', 'recepcionista', 'medico'] },
    { text: 'Profesionales',         icon: <LocalHospitalIcon />, path: '/medicos',  roles: ['admin'] },
    { text: 'Especialidades', icon: <MedicalServicesIcon />, path: '/especialidades', roles: ['admin'] },
    { text: 'Obras sociales', icon: <LocalHospitalIcon />,   path: '/obras-sociales', roles: ['admin'] },
    { text: 'Pacientes',    icon: <PeopleIcon />,            path: '/pacientes',      roles: ['admin', 'recepcionista'] },
    { text: 'Búsqueda HC',  icon: <FindInPageIcon />,        path: '/busqueda-hc',    roles: ['admin', 'medico'] },
    { text: 'Usuarios',     icon: <ManageAccountsIcon />,    path: '/staff',          roles: ['admin'] },
    { text: 'Configuración', icon: <SettingsIcon />,         path: '/configuracion',  roles: ['admin'] },
  ].filter(item => item.roles.includes(usuario.rol));

  function handleMenuClick(path) {
    navigate(path);
    if (isMobile) setOpen(false); // Cerrar drawer en mobile al navegar
  }

  function colorRol(rol) {
    const colores = { admin: 'error', medico: 'info', recepcionista: 'warning', paciente: 'success' };
    return colores[rol] || 'default';
  }

  function seleccionarTema(tema) {
    aplicarTema(tema);
    setTemaActual(tema.id);
    localStorage.setItem('tema_usuario_data', JSON.stringify(tema));
    setTemaAnchor(null);
  }

  const topOffset = avisos.length > 0 ? '96px' : '64px';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'var(--c-fondo)' }}>

      {/* ── AppBar ── */}
      <AppBar position="fixed" sx={{ zIndex: 1201, bgcolor: 'var(--c-superficie)' }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton color="inherit" onClick={() => setOpen(!open)} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" sx={{
            color: '#e0e0e0', fontWeight: 700, flexGrow: 1,
            fontSize: { xs: 15, sm: 20 },
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            Sistema Médico
          </Typography>

          {/* Chip de rol — oculto en mobile muy pequeño */}
          <Chip
            label={usuario.rol}
            color={colorRol(usuario.rol)}
            size="small"
            sx={{ mr: 1, display: { xs: 'none', sm: 'flex' } }}
          />

          {/* Nombre del usuario — oculto en mobile */}
          <Typography sx={{ color: '#b0b0b0', mr: 1, fontSize: 14, display: { xs: 'none', md: 'block' } }}>
            {usuario.nombre}
          </Typography>

          {/* Notificaciones en recepción (solo médico) */}
          {usuario.rol === 'medico' && (
            <IconButton
              onClick={e => setNotifAnchor(e.currentTarget)}
              sx={{ mr: 0.5, color: enRecepcion.length > 0 ? '#ffb74d' : '#b0b0b0' }}
            >
              <Badge badgeContent={enRecepcion.length} color="warning" invisible={enRecepcion.length === 0}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}

          <Popover
            open={Boolean(notifAnchor)}
            anchorEl={notifAnchor}
            onClose={() => setNotifAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: {
              bgcolor: 'var(--c-superficie)', color: '#e0e0e0',
              width: { xs: '90vw', sm: 320 },
              border: '1px solid var(--c-primario)',
            }}}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--c-primario)' }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#ffb74d' }}>
                Pacientes en recepción
              </Typography>
            </Box>
            {enRecepcion.length === 0 ? (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography sx={{ fontSize: 13, color: '#666' }}>Sin pacientes esperando.</Typography>
              </Box>
            ) : (
              <MuiList dense disablePadding>
                {enRecepcion.map((t, i) => (
                  <React.Fragment key={t.id}>
                    {i > 0 && <Divider sx={{ borderColor: 'var(--c-primario)' }} />}
                    <MuiListItem sx={{ py: 1, px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Box
                        onClick={() => {
                          if (t.paciente_id) {
                            navigate(`/historial/${t.paciente_id}?nueva=1&turno=${t.id}`);
                            setNotifAnchor(null);
                          }
                        }}
                        sx={{ flex: 1, cursor: t.paciente_id ? 'pointer' : 'default', borderRadius: 1, px: 1, py: 0.5,
                          '&:hover': t.paciente_id ? { bgcolor: 'rgba(255,255,255,0.06)' } : {} }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: t.paciente_id ? 'var(--c-acento)' : '#e0e0e0' }}>
                          {t.paciente_nombre || '—'}
                          {t.paciente_id && <Typography component="span" sx={{ fontSize: 10, ml: 0.5, color: '#90a4ae' }}>→ HC</Typography>}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#90a4ae' }}>
                          {t.paciente_dni ? `DNI ${t.paciente_dni} · ` : ''}
                          {t.hora_inicio ? String(t.hora_inicio).substring(0, 5) : ''}
                          {t.medico_nombre ? ` · ${t.medico_nombre}` : ''}
                        </Typography>
                      </Box>
                      <IconButton size="small" title="Marcar como atendido"
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/api/turnos/${t.id}`, { estado: 'atendido' });
                            setEnRecepcion(prev => prev.filter(x => x.id !== t.id));
                          } catch {}
                        }}
                        sx={{ bgcolor: 'rgba(102,187,106,0.15)', color: '#66bb6a', flexShrink: 0, '&:hover': { bgcolor: 'rgba(102,187,106,0.3)' } }}
                      >
                        <Typography sx={{ fontSize: 10, fontWeight: 700, px: 0.5 }}>✓</Typography>
                      </IconButton>
                    </MuiListItem>
                  </React.Fragment>
                ))}
              </MuiList>
            )}
          </Popover>

          {/* Selector de tema */}
          <IconButton
            onClick={e => setTemaAnchor(e.currentTarget)}
            sx={{ mr: 0.5, color: temaAnchor ? 'var(--c-acento)' : '#b0b0b0',
              '&:hover': { color: 'var(--c-acento)' } }}
            title="Cambiar tema de colores"
          >
            <PaletteIcon />
          </IconButton>
          <Popover
            open={Boolean(temaAnchor)}
            anchorEl={temaAnchor}
            onClose={() => setTemaAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: {
              bgcolor: 'var(--c-superficie)', color: '#e0e0e0',
              border: '1px solid var(--c-primario)',
              p: 2, minWidth: 220,
            }}}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--c-acento)', mb: 1.5, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Tema de colores
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              {TEMAS.map(t => (
                <Box
                  key={t.id}
                  onClick={() => seleccionarTema(t)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 1.5,
                    p: 1,
                    border: temaActual === t.id ? '2px solid var(--c-acento)' : '2px solid transparent',
                    bgcolor: t.color_fondo,
                    '&:hover': { border: '2px solid rgba(255,255,255,0.3)' },
                    transition: 'border 0.15s',
                  }}
                >
                  {/* Tira de colores del tema */}
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 0.8 }}>
                    {t.preview.map((c, i) => (
                      <Box key={i} sx={{ flex: 1, height: 14, borderRadius: 0.5, bgcolor: c }} />
                    ))}
                  </Box>
                  <Typography sx={{ fontSize: 11, fontWeight: temaActual === t.id ? 700 : 400, color: t.color_acento }}>
                    {t.nombre}
                    {temaActual === t.id && <span style={{ marginLeft: 4 }}>✓</span>}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: 10, color: '#666', mt: 1.5, textAlign: 'center' }}>
              Tu preferencia se guarda automáticamente
            </Typography>
          </Popover>

          {/* Chat */}
          {tieneChat && (
            <IconButton
              onClick={() => { setChatOpen(o => !o); setUnread(0); }}
              sx={{ mr: 0.5, color: chatOpen ? 'var(--c-acento)' : '#b0b0b0',
                bgcolor: chatOpen ? 'rgba(144,202,249,0.1)' : 'transparent',
                '&:hover': { color: 'var(--c-acento)' } }}
            >
              <Badge badgeContent={unread > 9 ? '9+' : unread} color="error" invisible={unread === 0}
                sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16, p: '0 3px' } }}>
                <ChatIcon />
              </Badge>
            </IconButton>
          )}

          <Avatar
            sx={{ bgcolor: 'var(--c-primario)', cursor: 'pointer', width: 32, height: 32, fontSize: 14 }}
            onClick={e => setAnchorEl(e.currentTarget)}
          >
            {usuario.nombre?.charAt(0)}
          </Avatar>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
            PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
            <MenuItem disabled sx={{ fontSize: 13, opacity: 0.7 }}>{usuario.nombre}</MenuItem>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
            <MenuItem onClick={onLogout} sx={{ color: '#f48fb1' }}>Cerrar sesión</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* ── Banner de avisos ── */}
      {avisos.length > 0 && (
        <Box sx={{
          position: 'fixed', top: { xs: 56, sm: 64 }, left: 0, right: 0, zIndex: 1200,
          bgcolor: TIPO_COLORES[avisos[0].tipo]?.bg || TIPO_COLORES.info.bg,
          color: TIPO_COLORES[avisos[0].tipo]?.text || TIPO_COLORES.info.text,
          overflow: 'hidden', height: 32, display: 'flex', alignItems: 'center',
        }}>
          <style>{`@keyframes marquee { 0% { transform: translateX(100vw); } 100% { transform: translateX(-100%); } }`}</style>
          <Box sx={{
            display: 'inline-block', whiteSpace: 'nowrap',
            animation: `marquee ${Math.max(28, avisos.map(a => a.texto.length).reduce((s, l) => s + l, 0) * 0.12)}s linear infinite`,
            fontSize: 12, fontWeight: 400, letterSpacing: 0.4, opacity: 0.92,
          }}>
            {avisos.map((a, i) => (
              <span key={a.id}>{a.texto}{i < avisos.length - 1 && <span style={{ margin: '0 40px', opacity: 0.5 }}>◆</span>}</span>
            ))}
            <span style={{ margin: '0 40px', opacity: 0.5 }}>◆</span>
            {avisos.map((a, i) => (
              <span key={`r-${a.id}`}>{a.texto}{i < avisos.length - 1 && <span style={{ margin: '0 40px', opacity: 0.5 }}>◆</span>}</span>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Drawer ── */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            bgcolor: 'var(--c-superficie)',
            color: '#e0e0e0',
            mt: isMobile ? 0 : (avisos.length > 0 ? '96px' : '64px'),
            transition: 'margin-top 0.2s',
          }
        }}
      >
        {/* En mobile mostramos un header con botón cerrar */}
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid var(--c-primario)' }}>
            <Typography sx={{ fontWeight: 700, color: 'var(--c-acento)', fontSize: 15 }}>Menú</Typography>
            <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: '#b0b0b0' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <List>
          {menu.map(item => (
            <ListItem button key={item.text} onClick={() => handleMenuClick(item.path)}
              title={item.text}
              sx={{
                bgcolor: location.pathname === item.path ? 'var(--c-primario)' : 'transparent',
                '&:hover': { bgcolor: 'var(--c-primario)' },
                borderRadius: 1, mx: 1, my: 0.5,
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'var(--c-acento)' : '#7a9bbf', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: 13,
                  fontWeight: location.pathname === item.path ? 700 : 400,
                  color: location.pathname === item.path ? 'var(--c-acento)' : '#b0bec5',
                  letterSpacing: location.pathname === item.path ? 0.2 : 0,
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* ── Panel chat — fullscreen en mobile ── */}
      {tieneChat && chatOpen && (
        <Box sx={{
          position: 'fixed',
          top: isMobile ? 0 : topOffset,
          right: isMobile ? 0 : 24,
          left: isMobile ? 0 : 'auto',
          bottom: isMobile ? 0 : 'auto',
          width: isMobile ? '100%' : 320,
          height: isMobile ? '100%' : `calc(100vh - ${topOffset === '96px' ? 120 : 88}px)`,
          bgcolor: 'var(--c-fondo)',
          border: '1px solid var(--c-primario)',
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          zIndex: isMobile ? 1300 : 1100,
        }}>
          <ChatPanel open={chatOpen} onClose={() => setChatOpen(false)} />
        </Box>
      )}

      {/* ── Contenido principal ── */}
      <Box component="main" sx={{
        flexGrow: 1,
        p: { xs: 1.5, sm: 3 },
        mt: avisos.length > 0 ? { xs: '88px', sm: '96px' } : { xs: '56px', sm: '64px' },
        ml: !isMobile && open ? `${drawerWidth}px` : 0,
        transition: 'margin 0.3s',
        color: '#e0e0e0',
        minWidth: 0, // Evita overflow en flex
      }}>
        {children}
      </Box>
    </Box>
  );
}
